import { CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import {
  createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode,
} from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'loading';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
  /** How far work going on has got, 0 to 1: drawn as a bar under the message. */
  progress?: number;
}

export interface ToastApi {
  success: (message: string, durationMs?: number) => number;
  error: (message: string, durationMs?: number) => number;
  info: (message: string, durationMs?: number) => number;
  /** Work going on in the background (a mail being sent): stays until dismissed. */
  loading: (message: string, progress?: number) => number;
  /**
   * Changes a toast in place (Plan BM): a mail being sent says how far it has got, then how it went,
   * in the same box, without one note leaving and another arriving. A finished one closes by itself.
   */
  update: (id: number, next: { type: ToastType; message: string; progress?: number }, durationMs?: number) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Non-blocking notifications. Never use alert() — see .claude/rules/ui-conventions.md. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const TOAST_STYLES: Record<ToastType, { Icon: typeof CheckCircle2; ring: string; icon: string }> = {
  success: { Icon: CheckCircle2, ring: 'border-emerald-200 dark:border-emerald-500/30', icon: 'text-emerald-700 dark:text-emerald-400' },
  error: { Icon: XCircle, ring: 'border-rose-200 dark:border-rose-500/30', icon: 'text-rose-600 dark:text-rose-400' },
  info: { Icon: Info, ring: 'border-brand-200 dark:border-brand-500/30', icon: 'text-brand-700 dark:text-brand-300' },
  loading: { Icon: Loader2, ring: 'border-slate-200 dark:border-navy-600', icon: 'animate-spin text-brand-700 dark:text-brand-300' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type: ToastType, message: string, durationMs?: number, progress?: number) => {
      const id = ++idRef.current;
      setToasts((ts) => [...ts, { id, type, message, ...(progress === undefined ? {} : { progress }) }]);
      // An error stays long enough to be read and acted on (WCAG 2.2.1); it can be closed sooner.
      durationMs ??= type === 'error' ? 10_000 : type === 'loading' ? 0 : 4_000;
      if (durationMs) setTimeout(() => remove(id), durationMs);
      return id;
    },
    [remove],
  );

  const toast = useMemo<ToastApi>(
    () => ({
      success: (m, d) => push('success', m, d),
      error: (m, d) => push('error', m, d),
      info: (m, d) => push('info', m, d),
      loading: (m, p) => push('loading', m, 0, p),
      update: (id, next, durationMs) => {
        setToasts((ts) => ts.map((t) => (t.id === id ? { id, ...next } : t)));
        durationMs ??= next.type === 'error' ? 10_000 : next.type === 'loading' ? 0 : 4_000;
        if (durationMs) setTimeout(() => remove(id), durationMs);
      },
      dismiss: remove,
    }),
    [push, remove],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Topmost layer: feedback raised inside a modal must be visible over it. */}
      {/* A live region present from the start (UX audit, 17/10/2026): one added together with
          its text is often not read. Each error is an alert, said at once. */}
      <section aria-label="Thông báo nhanh" aria-live="polite" className="fixed left-1/2 top-4 z-[140] flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 flex-col gap-2 md:left-auto md:right-6 md:top-20 md:w-[22.5rem] md:translate-x-0">
        {toasts.map((t) => {
          const s = TOAST_STYLES[t.type] ?? TOAST_STYLES.info;
          return (
            <div
              key={t.id}
              role={t.type === 'error' ? 'alert' : undefined}
              className="toast-in flex items-start gap-3 rounded-2xl border border-slate-100 bg-white p-3.5 pr-2 shadow-lg dark:border-navy-700 dark:bg-navy-800"
            >
              <s.Icon size={20} className={`mt-0.5 shrink-0 ${s.icon}`} />
              {/* A second line after a line break says what it was about (as drawn: who, then how much and which bill). */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-snug text-slate-800 dark:text-slate-100">{t.message.split('\n')[0]}</p>
                {t.message.includes('\n') && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{t.message.slice(t.message.indexOf('\n') + 1)}</p>}
                {t.type === 'loading' && t.progress !== undefined && (
                  <div
                    role="progressbar"
                    aria-label={t.message.split('\n')[0]}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(t.progress * 100)}
                    className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-navy-700"
                  >
                    <div className="h-full rounded-full bg-brand-600 transition-[width] duration-300 motion-reduce:transition-none dark:bg-brand-400" style={{ width: `${Math.max(4, Math.round(t.progress * 100))}%` }} />
                  </div>
                )}
              </div>
              <button
                onClick={() => remove(t.id)}
                aria-label="Đóng"
                className="shrink-0 rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:bg-navy-700"
              >
                <X size={16} />
              </button>
            </div>
          );
        })}
      </section>
    </ToastContext.Provider>
  );
}
